import React, { useState, useMemo, useEffect } from "react";
import ModalPortal from "@/components/Global/ModalPortal";
import ModalHeader from "./PrereqModalHeader";
import { SkillCardHeader } from "@/features/assignment_modal/components/SkillCardHeader";
import "./PrerequisiteCreateModal.scss";
import { StudentAvatar } from "@/features/dashboard/components/StudentAvatar/StudentAvatar";
import saveFileIcon from "@/images/icons/save-file-icon.svg";
import purpleCloseIcon from "@/images/icons/purple-close-icon.svg";
import { TrashIcon } from "./PrereqTrashIcon";
import StatusCard from "./StatusCard";
import prerequisiteIcon from "@/images/icons/prerequisite-skill-icon.svg";
import prereqPlusIcon from "@/images/icons/prereq-plus-icon.svg";
import { UserBadge } from "@/components/shared";
import StudentSelectionModal from "./StudentSelectionModal";
import useRosterInfo from "@/hooks/useRosterInfo";
import usePreTeachData from "@/hooks/usePreTeachData";

// Updated interfaces for the new structure
interface PrerequisiteSkill {
  skillId: string;
  displayName: string;
  timeToMaster?: number;
  exposureCount?: number;
  errorCount?: number;
  pacing?: string;
}

interface StudentWithPrerequisite {
  studentId: string;
  studentName: string;
  prerequisites: PrerequisiteSkill;
}

interface FocusSkillEntry {
  focusSkill: {
    skillId: string;
    displayName: string;
  };
  prerequisites: PrerequisiteSkill[];  // Array of all prerequisite skills
  students: StudentWithPrerequisite[];
}

interface PrerequisiteCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  skillData?: FocusSkillEntry;
  onAddStudent?: (prerequisiteSkillId: string, student: any) => void;
  onRemoveStudent?: (prerequisiteSkillId: string, studentId: string) => void;
  groupedPrerequisites?: Record<string, { prerequisite: any; students: any[] }>;
}

const PrerequisiteCreateModal = ({
  isOpen,
  onClose,
  skillData,
  onAddStudent,
  onRemoveStudent,
  groupedPrerequisites,
}: PrerequisiteCreateModalProps) => {

  // Add state for student selection modal
  const [isStudentSelectionOpen, setIsStudentSelectionOpen] = useState(false);
  const [selectedPrerequisiteForAddition, setSelectedPrerequisiteForAddition] = useState<PrerequisiteSkill | null>(null);

  // Get roster info for available students
  const { students } = useRosterInfo();
  const { getStudentMasteryForSkill } = usePreTeachData();

  // Create initial groups for ALL prerequisite skills
  const initialGroupedByPrerequisite = useMemo(() => {
    if (groupedPrerequisites) return groupedPrerequisites;

    if (!skillData || !skillData.prerequisites) {

      return {};
    }

    const groups: Record<string, { prerequisite: PrerequisiteSkill; students: StudentWithPrerequisite[] }> = {};

    // 1. Create groups for ALL prerequisite skills (even if no students need them)
    skillData.prerequisites.forEach(prereqSkill => {
      groups[prereqSkill.skillId] = {
        prerequisite: prereqSkill,
        students: []
      };
    });

    // 2. Add students to their matching prerequisite skill groups
    skillData.students.forEach(student => {
      const studentPrereqSkillId = student.prerequisites.skillId;
      if (groups[studentPrereqSkillId]) {
        groups[studentPrereqSkillId].students.push(student);
      } else {
        // If student has a prerequisite not in our main list, create a new group
        groups[studentPrereqSkillId] = {
          prerequisite: student.prerequisites,
          students: [student]
        };
      }
    });

    return groups;
  }, [skillData, groupedPrerequisites]);

  // Local state for temporary changes (only applied on "Apply")
  const [localChanges, setLocalChanges] = useState<{
    addedStudents: Record<string, any[]>;
    removedStudentIds: Set<string>;
  }>({
    addedStudents: {},
    removedStudentIds: new Set()
  });

  // Reset local changes when modal opens/closes or skillData changes
  useEffect(() => {
    setLocalChanges({
      addedStudents: {},
      removedStudentIds: new Set()
    });
  }, [isOpen, skillData]);

  // Compute current groups with local changes applied
  const currentGroupedByPrerequisite = useMemo(() => {
    const groups = { ...initialGroupedByPrerequisite };

    Object.keys(groups).forEach(skillId => {
      // Start with original students, remove any that were locally removed
      const filteredStudents = groups[skillId].students.filter(
        student => !localChanges.removedStudentIds.has(student.studentId)
      );

      // Add any locally added students
      const addedStudents = localChanges.addedStudents[skillId] || [];

      groups[skillId] = {
        ...groups[skillId],
        students: [...filteredStudents, ...addedStudents]
      };
    });

    return groups;
  }, [initialGroupedByPrerequisite, localChanges]);

  // Get all currently assigned student IDs to exclude them from selection
  const assignedStudentIds = useMemo(() => {
    const ids: string[] = [];
    Object.values(currentGroupedByPrerequisite || {}).forEach(group => {
      group.students.forEach(student => {
        ids.push(student.studentId);
      });
    });
    return ids;
  }, [currentGroupedByPrerequisite]);

  const onCancelHandler = () => {
    // Reset local changes and close
    setLocalChanges({
      addedStudents: {},
      removedStudentIds: new Set()
    });
    onClose();
  }

  const onSaveHandler = () => {
    // Apply all local changes to parent state
    Object.keys(localChanges.addedStudents).forEach(prerequisiteSkillId => {
      localChanges.addedStudents[prerequisiteSkillId].forEach(student => {
        if (onAddStudent) {
          onAddStudent(prerequisiteSkillId, student);
        }
      });
    });

    localChanges.removedStudentIds.forEach(studentId => {
      // Find which prerequisite this student belongs to and remove them
      Object.values(initialGroupedByPrerequisite).forEach(group => {
        const studentExists = group.students.find(s => s.studentId === studentId);
        if (studentExists && onRemoveStudent) {
          onRemoveStudent(group.prerequisite.skillId, studentId);
        }
      });
    });

    // Reset local changes and close
    setLocalChanges({
      addedStudents: {},
      removedStudentIds: new Set()
    });
    onClose();
  }

  const addStudentHandler = (prerequisiteSkill: PrerequisiteSkill) => {
    setSelectedPrerequisiteForAddition(prerequisiteSkill);
    setIsStudentSelectionOpen(true);
  };

  const handleStudentSelection = (selectedStudent: any) => {
    if (!selectedPrerequisiteForAddition) return;

    // Get real mastery data for this student and prerequisite skill
    const masteryData = getStudentMasteryForSkill(selectedStudent.studentId, selectedPrerequisiteForAddition.skillId);

    const newStudent = {
      studentId: selectedStudent.studentId,
      studentName: selectedStudent.studentName,
      prerequisites: {
        skillId: selectedPrerequisiteForAddition.skillId,
        displayName: selectedPrerequisiteForAddition.displayName,
        timeToMaster: selectedPrerequisiteForAddition.timeToMaster || 0,
        errorCount: masteryData.errorCount,
        exposureCount: masteryData.exposureCount,
        pacing: masteryData.pacing
      },
      timeToMaster: selectedPrerequisiteForAddition.timeToMaster || 0
    };

    // Add to local changes only
    setLocalChanges(prev => ({
      ...prev,
      addedStudents: {
        ...prev.addedStudents,
        [selectedPrerequisiteForAddition.skillId]: [
          ...(prev.addedStudents[selectedPrerequisiteForAddition.skillId] || []),
          newStudent
        ]
      }
    }));

    setSelectedPrerequisiteForAddition(null);
  };

  const handleCloseStudentSelection = () => {
    setIsStudentSelectionOpen(false);
    setSelectedPrerequisiteForAddition(null);
  };

  const removeStudentHandler = (prerequisiteSkillId: string, studentId: string) => {
    // Check if this is a locally added student
    const isLocallyAdded = localChanges.addedStudents[prerequisiteSkillId]?.some(
      s => s.studentId === studentId
    );

    if (isLocallyAdded) {
      // Remove from locally added students
      setLocalChanges(prev => ({
        ...prev,
        addedStudents: {
          ...prev.addedStudents,
          [prerequisiteSkillId]: prev.addedStudents[prerequisiteSkillId].filter(
            s => s.studentId !== studentId
          )
        }
      }));
    } else {
      // Mark original student for removal
      setLocalChanges(prev => ({
        ...prev,
        removedStudentIds: new Set([...prev.removedStudentIds, studentId])
      }));
    }
  };

  const getDomainFromSkillId = (skillId: string): string => {
    const domain = skillId.split('.')[0];
    return ['PA', 'D', 'HFW', 'BK', 'V', 'SR'].includes(domain) ? domain : 'default';
  };

  if (!isOpen) {
    return null;
  }

  return (
    <>
      <ModalPortal
        size="content"
        isOpen={isOpen}
        onClose={onCancelHandler}
        style={{ borderRadius: "0.75rem", border: "1px solid #36323B" }}
      >
        <div className="prereq-modal d-flex flex-column align-items-start">
          <ModalHeader title="Edit Readiness Gaps to Address" onClose={onCancelHandler} />
          <div className="d-flex justify-content-center w-100">
            <div className="prereq-divider-light d-flex justify-content-center align-items-center"></div>
          </div>
          <div className="prereq-body prereq-body-scrollable d-flex flex-column align-items-start gap-3 p-4 w-100">
            <div className="prereq-body-title">Skill from upcoming lesson</div>
            <SkillCardHeader
              skillName={skillData?.focusSkill?.displayName || ""}
              standards={""}
              domain={getDomainFromSkillId(skillData?.focusSkill?.skillId || "")}
              source={""}
              skillNameAbbreviation={true}
              showActionCard={false}
            />
            <div className="d-flex justify-content-center w-100">
              <div className="prereq-divider w-100 d-flex justify-content-center align-items-center"></div>
            </div>

            {Object.values(currentGroupedByPrerequisite || {}).map((group, groupIndex) => {
              const allStudents = group.students;

              return (
                <React.Fragment key={groupIndex}>
                  {/* Prerequisite Skill Header */}
                  <div className="d-flex flexalign-items-start justify-content-between gap-2 w-100">
                    <div className="prereq-body-title d-flex align-items-center gap-2">
                      <img src={prerequisiteIcon} alt="prerequisite" />
                      <div className={`prereq-body-title-skill-block prereq-body-title-skill-block--${getDomainFromSkillId(group.prerequisite.skillId)}`}>
                        {getDomainFromSkillId(group.prerequisite.skillId)}
                      </div>
                      <span>{group.prerequisite.displayName}</span>
                    </div>
                    <span className="prereq-body-title-count">{allStudents.length}</span>
                  </div>

                  {/* Students who need this prerequisite */}
                  {allStudents.length > 0 && (
                    allStudents.map((student, studentIndex) => {
                      // NEW: Get real mastery data for this student and prerequisite skill
                      const masteryData = getStudentMasteryForSkill(student.studentId, student.prerequisites.skillId);

                      return (
                        <div
                          className="prereq-body-student-row d-flex align-items-center gap-4 w-100"
                          key={`${groupIndex}-${studentIndex}`}
                        >
                          <div className="prereq-body-student-row-avatar d-flex align-items-center">
                            <UserBadge name={student.studentName} />
                            <span className="prereq-body-student-row-name d-flex justify-content-center align-items-center">
                              {student.studentName}
                            </span>
                          </div>
                          <div className="d-flex align-items-center gap-4">
                            <div className="d-flex flex-column align-items-center gap-4 flex-1">
                              <StatusCard
                                status={masteryData.pacing}
                                masteryStart={masteryData.pacing}
                                masteryEnd={masteryData.pacing}
                                observations={masteryData.exposureCount}
                                errors={masteryData.errorCount}
                                showGraph={false}
                                preReqModal={true}
                              />
                            </div>
                          </div>
                          <button
                            className="prereq-body-student-row-unassign"
                            onClick={() => removeStudentHandler(group.prerequisite.skillId, student.studentId)}
                          >
                            <TrashIcon />
                          </button>
                        </div>
                      );
                    })
                  )}
                  {/* Add Student button */}
                  <button
                    className="add-student-button"
                    onClick={() => addStudentHandler(group.prerequisite)}
                  >
                    <img src={prereqPlusIcon} alt="add student" />
                    <span>Add Student</span>
                  </button>

                  {/* Divider between groups */}
                  {groupIndex < Object.values(currentGroupedByPrerequisite || {}).length - 1 && (
                    <div className="d-flex justify-content-center w-100">
                      <div className="prereq-divider w-100 d-flex justify-content-center align-items-center"></div>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* Footer with Cancel/Apply */}
          <div className="prereq-footer d-flex justify-content-between w-100">
            <button className="prereq-footer-cancel d-flex justify-content-center align-items-center" onClick={onCancelHandler}>
              <img src={purpleCloseIcon} alt="close" />
              Cancel
            </button>
            <button className="prereq-footer-create d-flex justify-content-center align-items-center" onClick={onSaveHandler}>
              <img src={saveFileIcon} alt="save file" />
              Apply
            </button>
          </div>
        </div>
      </ModalPortal>

      {/* Student Selection Modal */}
      <StudentSelectionModal
        isOpen={isStudentSelectionOpen}
        onClose={handleCloseStudentSelection}
        onSelectStudent={handleStudentSelection}
        excludeStudentIds={assignedStudentIds}
        prerequisiteSkillName={selectedPrerequisiteForAddition?.displayName || ""}
        prerequisiteSkillId={selectedPrerequisiteForAddition?.skillId || ""}
      />
    </>
  );
};

export default PrerequisiteCreateModal;
